import { StyledSelect } from "@ando/ui";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRightLeft, Loader2 } from "lucide-react";
import * as React from "react";

import type { AdminOrganization } from "@ando/core";
import { listAdminOrgs, migrateAdminUser } from "@ando/core";
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  useToast,
} from "@ando/ui";

import { adminOrgKeys } from "@/features/admin/hooks/useAdminOrg";

/**
 * MigrateUserDialog — relocate ONE user from this org to another tenant.
 *
 * Wraps ``POST /platform-admin/users/{user_id}/migrate`` — the single
 * legitimate cross-tenant transition. The target picker is fed by
 * ``GET /platform-admin/organizations`` (one big page of 100; pagination
 * on the picker would be over-engineering and matches v1).
 *
 * The current org is filtered out of the picker (the BE rejects
 * migration to the same org anyway, and surfacing it just invites
 * accidental clicks). The "migra dati" checkbox controls whether the
 * per-row data follows the user — almost always yes; split-from-org-data
 * is a niche admin operation.
 *
 * Props mirror the P2 stub (``memberLabel``) so OrgMembersTab's existing
 * call site keeps working without a refactor.
 */

export interface MigrateUserDialogProps {
  /** UUID of the user being migrated. */
  userId: string;
  /** Org currently owning the user — filtered out of the target picker. */
  currentOrgId: string;
  /** Visible label used in the dialog copy (display name or email). */
  memberLabel: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface FormState {
  targetOrgId: string;
  migrateData: boolean;
}

const INITIAL: FormState = { targetOrgId: "", migrateData: true };

export function MigrateUserDialog({
  userId,
  currentOrgId,
  memberLabel,
  open,
  onOpenChange,
}: MigrateUserDialogProps): React.ReactElement {
  const { i18n } = useLingui();
  const { toast } = useToast();
  const qc = useQueryClient();

  const [form, setForm] = React.useState<FormState>(INITIAL);

  React.useEffect(() => {
    if (open) setForm(INITIAL);
  }, [open]);

  // Pull active orgs only — moving a user INTO a disabled tenant is not
  // actionable. The cache key (``adminOrgKeys.migratePicker``) is
  // deliberately distinct from the AdminOrgs list-page key so it never
  // gets evicted by the standard ``adminOrgKeys.list`` invalidations.
  const orgsQuery = useQuery({
    queryKey: adminOrgKeys.migratePicker,
    queryFn: ({ signal }) => listAdminOrgs({ limit: 100, activeFilter: "active" }, signal),
    enabled: open,
    staleTime: 30_000,
  });

  const targets = React.useMemo<AdminOrganization[]>(() => {
    const items = orgsQuery.data?.items ?? [];
    return items.filter((o) => o.id !== currentOrgId);
  }, [orgsQuery.data, currentOrgId]);

  const mutation = useMutation({
    mutationFn: () =>
      migrateAdminUser(userId, {
        target_org_id: form.targetOrgId,
        migrate_data: form.migrateData,
      }),
    onSuccess: () => {
      toast.success(i18n._(msg`Utente migrato`), {
        description: form.migrateData
          ? i18n._(msg`Dati e accessi spostati nella nuova organizzazione.`)
          : i18n._(msg`Utente spostato (dati lasciati nell'org sorgente).`),
      });
      qc.invalidateQueries({ queryKey: adminOrgKeys.members(currentOrgId) });
      qc.invalidateQueries({
        queryKey: adminOrgKeys.members(form.targetOrgId),
      });
      qc.invalidateQueries({ queryKey: adminOrgKeys.list });
      onOpenChange(false);
    },
    onError: (err) => {
      const message = err instanceof Error ? err.message : i18n._(msg`Errore inatteso.`);
      // BE error map: 409 owner_cannot_migrate / already_in_target_org —
      // each gets a tailored description so the admin knows how to fix it.
      if (message.includes("owner_cannot_migrate")) {
        toast.error(i18n._(msg`Owner non migrabile`), {
          description: i18n._(
            msg`Trasferisci la ownership dell'org sorgente prima di migrare l'utente.`,
          ),
        });
      } else if (message.includes("already_in_target_org")) {
        toast.error(i18n._(msg`Già nell'org target`), {
          description: i18n._(msg`L'utente è già membro dell'organizzazione scelta.`),
        });
      } else {
        toast.error(i18n._(msg`Migrazione fallita`), { description: message });
      }
    },
  });

  const canSubmit = form.targetOrgId.length > 0 && !mutation.isPending && !orgsQuery.isLoading;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) mutation.mutate();
          }}
          className="space-y-4"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft size={16} aria-hidden />
              {i18n._(msg`Migra utente`)}
            </DialogTitle>
            <DialogDescription>
              {i18n._(msg`Sposta`)} <strong>{memberLabel}</strong>{" "}
              {i18n._(
                msg`in un'altra organizzazione. È l'unico modo sicuro: dati e connessioni seguono l'utente atomicamente, l'azione viene loggata.`,
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="space-y-1.5">
              <label
                htmlFor="migrate-target-org"
                className="block font-semibold text-[10px] text-ink-3 uppercase tracking-wide"
              >
                {i18n._(msg`Organizzazione destinazione`)}
              </label>
              <StyledSelect
                id="migrate-target-org"
                value={form.targetOrgId}
                onChange={(e) => setForm((f) => ({ ...f, targetOrgId: e.target.value }))}
                disabled={orgsQuery.isLoading || mutation.isPending}
                className="h-9 w-full rounded-md border border-hairline bg-card px-2 text-[13px] text-ink-1 focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 disabled:opacity-60"
              >
                <option value="">
                  {orgsQuery.isLoading
                    ? i18n._(msg`Caricamento…`)
                    : targets.length === 0
                      ? i18n._(msg`Nessuna org disponibile`)
                      : i18n._(msg`Seleziona un'organizzazione`)}
                </option>
                {targets.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name} · {o.slug}
                  </option>
                ))}
              </StyledSelect>
              {orgsQuery.isError ? (
                <p className="text-[11px] text-destructive">
                  {i18n._(msg`Errore nel caricamento delle organizzazioni.`)}
                </p>
              ) : null}
            </div>

            <label className="flex items-start gap-2 rounded-md border border-hairline bg-card/55 px-3 py-2 text-[12.5px] text-ink-1">
              <Checkbox
                className="mt-0.5"
                checked={form.migrateData}
                onChange={(e) => setForm((f) => ({ ...f, migrateData: e.target.checked }))}
                disabled={mutation.isPending}
              />
              <span className="space-y-1">
                <span className="block font-medium text-ink-1">
                  {i18n._(msg`Migra anche i dati`)}
                </span>
                <span className="block text-[11px] text-ink-3">
                  {i18n._(
                    msg`Conversazioni, memoria, documenti, automazioni e OAuth seguono l'utente. Le connessioni legacy potrebbero richiedere un re-OAuth. Disattiva per lasciare i dati nell'org sorgente.`,
                  )}
                </span>
              </span>
            </label>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              {i18n._(msg`Annulla`)}
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {mutation.isPending ? (
                <Loader2 size={14} className="mr-2 animate-spin" aria-hidden />
              ) : null}
              {i18n._(msg`Migra`)}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
